import { useCallback } from 'react'
import {
  ApplicationVerifier,
  ConfirmationResult,
  signInWithPhoneNumber,
} from 'firebase/auth'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import {
  AUTH_TIMEOUT,
  handleAuthError,
  isNavigatorOnline,
  withAuthTimeout,
  validatePhoneNumber,
} from '../../Utils'
import {
  ANALYTICS_TAGS,
  AUTHENTICATION_CONSTANTS,
  PHONE_ERROR_MESSAGES,
} from '../../Constant'
import { useRecaptcha } from './useRecaptcha'

/**
 * Phone authentication hook - Try invisible reCAPTCHA first, fallback to visible reCAPTCHA
 * 1. Call signInWithPhoneNumber with invisible reCAPTCHA verifier
 * 2. Firebase tries invisible reCAPTCHA first
 * 3. If security restrictions apply, Firebase shows visible reCAPTCHA
 * 4. User solves visible challenge, callback fires
 * 5. signInWithPhoneNumber promise resolves with ConfirmationResult
 * 6. User enters SMS code to complete authentication
 */
export const usePhoneAuth = (
  shouldInitializeRecaptcha = true,
  toggleShowSnackbar: (snackbarData: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void
) => {
  const handleRecaptchaCallback = useCallback(() => {
    // Firebase handles the flow automatically after reCAPTCHA is solved
  }, [])

  const { ready, waitForVerifier, ensureRendered } = useRecaptcha(
    AUTHENTICATION_CONSTANTS.PHONE_SIGNIN_BTN,
    shouldInitializeRecaptcha,
    handleRecaptchaCallback
  )

  const sendPhoneVerification = useCallback(
    async (phoneNumber: string): Promise<ConfirmationResult | null> => {
      try {
        if (!validatePhoneNumber(phoneNumber)) {
          handleAuthError(
            ANALYTICS_TAGS.login_error,
            PHONE_ERROR_MESSAGES.INVALID_PHONE_NUMBER,
            'sendPhoneVerification',
            {
              provider: 'phone',
            }
          )
          toggleShowSnackbar({
            text: PHONE_ERROR_MESSAGES.INVALID_PHONE_NUMBER,
            color: 'red',
          })
          return null
        }

        if (!isNavigatorOnline()) {
          toggleShowSnackbar({
            text: PHONE_ERROR_MESSAGES.NETWORK_REQUIRED,
            color: 'red',
          })
          return null
        }

        const auth = await withAuthTimeout(
          getFirAuth(),
          AUTH_TIMEOUT,
          'getFirAuth for phone verification'
        )

        if (!auth) {
          handleAuthError(
            ANALYTICS_TAGS.auth_change_error,
            'Firebase auth not available',
            'sendPhoneVerification'
          )
          toggleShowSnackbar({
            text: PHONE_ERROR_MESSAGES.SERVICE_UNAVAILABLE,
            color: 'red',
          })
          return null
        }

        // Prepare reCAPTCHA and send verification
        const verifier = await waitForVerifier()
        await ensureRendered()

        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phoneNumber,
          verifier as ApplicationVerifier
        )

        return confirmationResult
      } catch (error) {
        handleAuthError(ANALYTICS_TAGS.login_error, error, 'sendPhoneVerification', {
          provider: 'phone',
        })
        toggleShowSnackbar({
          text: PHONE_ERROR_MESSAGES.VERIFICATION_FAILED,
          color: 'red',
        })
        return null
      }
    },
    [waitForVerifier, ensureRendered, toggleShowSnackbar]
  )

  return { sendPhoneVerification, recaptchaReady: ready }
}
